import { useEffect, useState } from 'react';
import { PlatformApiError } from '@sfc/sdk';
import { platform } from '../api/client';

/**
 * A password, for the one thing a long session cannot do.
 *
 * Sessions last six months and roll, so somebody who uses this at all is
 * never asked again on their own phone. What that cannot help is a second
 * device, and this is how somebody opens the product on a laptop without
 * leaving it, opening their mail and hoping.
 *
 * Framed as an option rather than a step, and the screen says what it is for
 * rather than asking for it as though an account is incomplete without one.
 * Nothing here is required, and removing it takes nothing away: the link was
 * the way in before and remains the way back.
 */
export function PasswordSection(): React.JSX.Element {
  const [has, setHas] = useState<boolean>();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [current, setCurrent] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string>();
  const [done, setDone] = useState<string>();

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const state = await platform.match.passwordState();
        if (live) setHas(state.hasPassword);
      } catch {
        // Not knowing is not an error worth a sentence: the section simply
        // waits rather than telling somebody about a request they did not make.
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  async function save(): Promise<void> {
    setBusy(true);
    setProblem(undefined);
    setDone(undefined);
    try {
      await platform.match.setPassword(password, has ? current : undefined);
      setHas(true);
      setOpen(false);
      setPassword('');
      setCurrent('');
      setDone('Saved. You can sign in with it on any device.');
    } catch (error) {
      setProblem(
        error instanceof PlatformApiError ? error.message : 'That did not save. Try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function remove(): Promise<void> {
    setBusy(true);
    setProblem(undefined);
    try {
      await platform.match.removePassword();
      setHas(false);
      setDone('Removed. We will email you a link when you need to sign in.');
    } catch (error) {
      setProblem(
        error instanceof PlatformApiError ? error.message : 'That did not work. Try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  if (has === undefined) return <></>;

  return (
    <section className="password" aria-label="Password">
      <h3 className="card__label">Signing in</h3>

      <p className="fine" data-testid="password-state">
        {has
          ? 'You have a password, so you can sign in on a new device without waiting for an email.'
          : 'We email you a link whenever you need to sign in. Set a password and you will not have to wait for one on a new device.'}
      </p>

      {done ? (
        <p className="notice notice--good" role="status" data-testid="password-done">
          {done}
        </p>
      ) : null}
      {problem ? (
        <p className="notice notice--problem" role="status" data-testid="password-problem">
          {problem}
        </p>
      ) : null}

      {open ? (
        <>
          {has ? (
            <label className="field">
              <span>Your current password</span>
              <input
                type="password"
                autoComplete="current-password"
                value={current}
                data-testid="current-password"
                onChange={(event) => setCurrent(event.target.value)}
              />
            </label>
          ) : null}

          <label className="field">
            <span>{has ? 'Your new password' : 'A password'}</span>
            <input
              type="password"
              autoComplete="new-password"
              minLength={10}
              value={password}
              placeholder="At least ten characters"
              data-testid="new-password"
              onChange={(event) => setPassword(event.target.value)}
            />
            <span className="fine">
              Ten characters or more. No symbols required — length is the part that helps.
            </span>
          </label>

          <div className="choices">
            <button className="button" type="button" disabled={busy} onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button
              className="button button--primary"
              type="button"
              disabled={busy || password.trim().length < 10}
              data-testid="save-password"
              onClick={() => void save()}
            >
              {busy ? 'Saving…' : 'Save it'}
            </button>
          </div>
        </>
      ) : (
        <div className="password__actions">
          <button
            className="button"
            type="button"
            disabled={busy}
            data-testid="set-password"
            onClick={() => setOpen(true)}
          >
            {has ? 'Change it' : 'Set a password'}
          </button>
          {has ? (
            <button
              className="button button--quiet"
              type="button"
              disabled={busy}
              data-testid="remove-password"
              onClick={() => void remove()}
            >
              Remove it
            </button>
          ) : null}
        </div>
      )}
    </section>
  );
}
